/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U2202ConnectedUser } from "./U2202Service";

export type U2202InitLU = U2000LUState["personalization"] & {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
};

export interface U2202ContextType {
  initLU: U2202InitLU;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U2202ConnectedUser>;
}

interface U2202ContextProviderProps {
  initLU: U2202InitLU;
  children: ReactNode;
}

const U2202Context = React.createContext<U2202ContextType | undefined>(undefined);

export const U2202ContextProvider: React.FC<U2202ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as U2202InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: "u2202",
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U2202ConnectedUser>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });

  const contextValue = useMemo<U2202ContextType>(
    () => ({
      initLU,
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
    }),
    [
      initLU,
      U2000GridStateHandlers,
      U2000SearchStateHandlers,
      U2000LUStateHandlers,
      U2000GridStateHandlers.getData?.()?.length,
      U2000GridStateHandlers.getTotalRows?.(),
    ]
  );

  return <U2202Context.Provider value={contextValue}>{children}</U2202Context.Provider>;
};

export function useU2202Context(): U2202ContextType {
  const context = useContext(U2202Context);
  if (!context) {
    throw new Error("useU2202Context must be used within a U2202ContextProvider");
  }
  return context;
}
